import { useEffect } from 'react';
import styled from 'styled-components';
import { ReactComponent as InfoIcon } from '@elementary/common-ui/assets/info-icon.svg';
import {
  Flex,
  FlexColumn,
  FlexSpace,
  GenerateAIButton,
} from '@elementary/common-ui/components';
import useAccount from '@elementary/common-ui/hooks/useAccount';
import { capitalize } from '@elementary/common-ui/utils/common';
import { Divider, Tooltip, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useFormContext } from 'react-hook-form';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import AiAccessAlert from 'catalog/components/AiAccessAlert';
import * as api from '../../../services/api';
import BulkEditTable from '../BulkEditTable';
import { BulkEditArrayProperty, BulkEditFormData } from '../types';
import ValuesControlList from './ValuesControlList';

const Wrapper = styled(Flex)`
  height: 100%;
`;

const Section = styled(FlexColumn)`
  padding: 24px;
  gap: 24px;
`;

const SuggestedChangesStep = ({ isLoading }: { isLoading: boolean }) => {
  const { watch, setValue } = useFormContext<BulkEditFormData>();
  const { tableData, editProperty, editPropertyType } = watch();
  const selectedEnvId = useSelectedEnv();
  const { data: account } = useAccount();

  const showValuesControlSection = editPropertyType === 'array';
  const showGenerateAllButton = editProperty === 'description';

  const {
    mutateAsync: generateDescriptions,
    isLoading: isGenerateDescriptionsLoading,
  } = useMutation(async (assetIds: string[]) =>
    api.generateAssetsDescription(
      {
        asset_ids: assetIds,
      },
      selectedEnvId!,
    ),
  );

  const handleGenerateDescriptions = async () => {
    const descriptions = await generateDescriptions(
      tableData.map((model) => model.unique_id),
    );

    setValue(
      'tableData',
      tableData.map((model) => ({
        ...model,
        description: descriptions[model.unique_id],
        isGenerated: true,
      })),
    );
  };

  const disabledAiTooltip = account?.ai_features_enabled
    ? undefined
    : 'Access to AI features and agents required';

  useEffect(() => {
    setValue('isGenerateAllLoading', isGenerateDescriptionsLoading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGenerateDescriptionsLoading]);

  return (
    <Wrapper divider={<Divider orientation="vertical" light />}>
      {showValuesControlSection && (
        <Section>
          <FlexColumn sx={{ gap: 1 }}>
            <Typography variant="h3">
              Current {capitalize(editProperty)}
            </Typography>
            <Typography sx={{ color: 'common.gray2' }}>
              The selected rows contain the following {editProperty}:
            </Typography>
          </FlexColumn>
          <ValuesControlList
            editProperty={editProperty as BulkEditArrayProperty}
            isLoading={isLoading}
          />
        </Section>
      )}

      <Section>
        <FlexSpace>
          <FlexColumn sx={{ gap: 1 }}>
            <Typography variant="h3">Preview</Typography>
            <Flex sx={{ alignItems: 'center', gap: 1 }}>
              <Flex>
                <InfoIcon />
              </Flex>
              <Typography sx={{ color: 'common.gray2' }}>
                These assets can only be edited in code. The following changes
                will be applied upon PR approval
              </Typography>
            </Flex>
          </FlexColumn>

          {showGenerateAllButton && (
            <FlexColumn sx={{ gap: 1 }}>
              <Tooltip title={disabledAiTooltip}>
                <div>
                  <GenerateAIButton
                    onClick={handleGenerateDescriptions}
                    loading={isGenerateDescriptionsLoading}
                    disabled={!!disabledAiTooltip}
                  >
                    {isGenerateDescriptionsLoading
                      ? 'Processing request...'
                      : 'Generate AI Descriptions'}
                  </GenerateAIButton>
                </div>
              </Tooltip>
              <AiAccessAlert variant="infoLabel" width="270px" />
            </FlexColumn>
          )}
        </FlexSpace>
        <BulkEditTable isLoading={isLoading} />
      </Section>
    </Wrapper>
  );
};

export default SuggestedChangesStep;
